import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { AuthState, OktaAuth } from '@okta/okta-auth-js';
import { fromEvent, merge, Subject, Subscription, timer } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { OktaAuthService } from './okta-auth.service';

export const INACTIVITY_TIMEOUT_MINUTES = 15;

export const INACTIVITY_TIMEOUT = new InjectionToken<number>('INACTIVITY_TIMEOUT', {
    providedIn: 'root',
    factory: () => INACTIVITY_TIMEOUT_MINUTES * 60 * 1000
});

const ACTIVITY_EVENTS = [
    'mousemove',
    'click',
    'keydown',
    'scroll',
    'touchstart',
    'touchmove'
] as const;

@Injectable({
    providedIn: 'root'
})
export class InactivityService implements OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private activitySubscription?: Subscription;
    private timeoutSubscription?: Subscription;
    private authStateHandler: (authState: AuthState) => void;
    private isStarted = false;
    private isMonitoring = false;
    private isLogoutInProgress = false;

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
        @Inject(INACTIVITY_TIMEOUT) private readonly inactivityTimeout: number,
        private readonly oktaAuthService: OktaAuthService,
        private readonly router: Router
    ) {
        this.authStateHandler = (authState: AuthState): void => {
            this.updateMonitoring(Boolean(authState.isAuthenticated));
        };
    }

    start(): void {
        if (this.isStarted) {
            return;
        }

        this.isStarted = true;
        this.oktaAuth.authStateManager.subscribe(this.authStateHandler);
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => void this.syncAuthenticationState());
        void this.syncAuthenticationState();
    }

    stop(): void {
        this.isMonitoring = false;
        this.activitySubscription?.unsubscribe();
        this.timeoutSubscription?.unsubscribe();
        this.activitySubscription = undefined;
        this.timeoutSubscription = undefined;
    }

    ngOnDestroy(): void {
        this.stop();
        if (this.isStarted) {
            this.oktaAuth.authStateManager.unsubscribe(this.authStateHandler);
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    private async syncAuthenticationState(): Promise<void> {
        try {
            this.updateMonitoring(await this.oktaAuth.isAuthenticated());
        } catch {
            this.updateMonitoring(false);
        }
    }

    private updateMonitoring(isAuthenticated: boolean): void {
        if (isAuthenticated) {
            this.startMonitoring();
            return;
        }

        this.stop();
    }

    private startMonitoring(): void {
        if (this.isMonitoring) {
            this.resetTimer();
            return;
        }

        this.isMonitoring = true;
        this.activitySubscription = merge(
            ...ACTIVITY_EVENTS.map(eventName => fromEvent(this.document, eventName, { capture: true }))
        )
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.resetTimer());
        this.resetTimer();
    }

    private resetTimer(): void {
        if (!this.isMonitoring) {
            return;
        }

        this.timeoutSubscription?.unsubscribe();
        this.timeoutSubscription = timer(this.inactivityTimeout)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => void this.logoutInactiveUser());
    }

    private async logoutInactiveUser(): Promise<void> {
        if (this.isLogoutInProgress) {
            return;
        }

        this.isLogoutInProgress = true;
        this.stop();

        try {
            await this.oktaAuthService.logout();
            await this.router.navigate(['/session-expired']);
        } finally {
            this.isLogoutInProgress = false;
        }
    }
}
